import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { SectionContainer } from 'components/DetailsView/Section';
import { TotemLabel } from 'components/TotemLabel';
import { useHasPermission } from 'hooks/useHasPermission';

import { SupplyOrderProductIssueType, useGetProductsByIdQuery, TotadminPermission } from 'data/__generated__';

import { SUPPLY_ORDER_ISSUE_LABELS } from '../../constants/issueLabels';
import { getSupplyOrderProductReceivedTotal } from '../../shared/receivedDetails';
import { SupplyOrderDetails } from '../supplyOrderFormValues';

type Issue = SupplyOrderDetails['issues'][number];

/**
 * Les anomalies déclarées au contrôle de réception, groupées par nature.
 *
 * Section repliée : `lazy`, les noms de produits ne sont chargés qu'à l'ouverture. Elle n'est PAS verrouillée
 * par `ProductsRead` — les anomalies appartiennent à l'achat ; sans le droit, on affiche l'identifiant.
 */
export const IssuesSection = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => {
    const { issues } = supplyOrder;

    if (!issues.length) {
        return (
            <SectionContainer title="Anomalies de réception">
                <p className="text-sm text-muted-foreground">Aucune anomalie déclarée sur cet achat.</p>
            </SectionContainer>
        );
    }

    return (
        <SectionContainer title={`Anomalies de réception (${issues.length})`} lazy>
            <IssuesContent supplyOrder={supplyOrder} />
        </SectionContainer>
    );
};

const IssuesContent = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => {
    const { issues } = supplyOrder;
    const productIds = issues.flatMap((issue) => ('productId' in issue && issue.productId ? [issue.productId] : []));

    const canReadProducts = useHasPermission(TotadminPermission.ProductsRead);
    const { data } = useGetProductsByIdQuery({
        variables: { productIds },
        skip: !productIds.length || !canReadProducts,
    });

    // Le nom du produit est un confort : tant qu'il n'est pas arrivé (ou sans le droit de le lire), on
    // affiche l'identifiant plutôt que de masquer toute la section.
    const productNameById = new Map((data?.getProductsById ?? []).map(({ _id, fullname }) => [_id, fullname]));
    const getProductName = (productId: string) => productNameById.get(productId) ?? productId;

    const issuesByType = new Map<SupplyOrderProductIssueType, Issue[]>();
    issues.forEach((issue) => {
        if (!issue.type) return;
        issuesByType.set(issue.type, [...(issuesByType.get(issue.type) ?? []), issue]);
    });

    return (
        <div className="flex flex-col gap-6">
            {[...issuesByType.entries()].map(([type, typeIssues]) => (
                <div key={type} className="flex flex-col gap-2">
                    <TotemLabel>{SUPPLY_ORDER_ISSUE_LABELS[type]}</TotemLabel>
                    <IssueTable
                        type={type}
                        issues={typeIssues}
                        getProductName={getProductName}
                        supplyOrder={supplyOrder}
                    />
                </div>
            ))}
        </div>
    );
};

const IssueTable = ({
    type,
    issues,
    getProductName,
    supplyOrder,
}: {
    type: SupplyOrderProductIssueType;
    issues: Issue[];
    getProductName: (productId: string) => string;
    supplyOrder: SupplyOrderDetails;
}) => {
    const columns = ISSUE_COLUMNS[type];

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    {columns.map((column) => (
                        <TableHead key={column}>{column}</TableHead>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {issues.map((issue, index) => (
                    <TableRow key={index}>
                        {getIssueCells({ type, issue, getProductName, supplyOrder }).map((cell, cellIndex) => (
                            <TableCell key={cellIndex}>{cell}</TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

const ISSUE_COLUMNS: Record<SupplyOrderProductIssueType, string[]> = {
    [SupplyOrderProductIssueType.NotReceived]: ['Produit'],
    [SupplyOrderProductIssueType.BadExpiryDate]: ['Produit', 'Unité(s)', 'DLC', 'Garantie fournisseur'],
    [SupplyOrderProductIssueType.DifferentQuantity]: ['Produit', 'Quantité attendue', 'Quantité reçue'],
    [SupplyOrderProductIssueType.DifferentPackaging]: ['Produit', 'Colisage attendu', 'Colisage reçu'],
    [SupplyOrderProductIssueType.BrokenQuantity]: [
        'Produit',
        'Unité(s) reçue(s)',
        'Unité(s) cassée(s)',
        'Vrai stock reçu',
    ],
    [SupplyOrderProductIssueType.NewBarcode]: ['Produit', 'Nouveau code barre'],
    [SupplyOrderProductIssueType.WrongProduct]: ['Produit', 'ID', 'Code barre'],
    [SupplyOrderProductIssueType.UnknownProduct]: ['Description', 'Code barre'],
};

function getIssueCells({
    type,
    issue,
    getProductName,
    supplyOrder,
}: {
    type: SupplyOrderProductIssueType;
    issue: Issue;
    getProductName: (productId: string) => string;
    supplyOrder: SupplyOrderDetails;
}): React.ReactNode[] {
    switch (type) {
        case SupplyOrderProductIssueType.NotReceived:
            return 'productId' in issue ? [getProductName(issue.productId)] : [];
        case SupplyOrderProductIssueType.BadExpiryDate:
            return 'minSupplierExpiryDays' in issue
                ? [getProductName(issue.productId), issue.quantity, issue.expiryDate, issue.minSupplierExpiryDays]
                : [];
        case SupplyOrderProductIssueType.DifferentQuantity:
            return 'expectedQuantity' in issue
                ? [getProductName(issue.productId), issue.expectedQuantity, issue.receivedQuantity]
                : [];
        case SupplyOrderProductIssueType.DifferentPackaging:
            return 'expectedPackaging' in issue
                ? [getProductName(issue.productId), issue.expectedPackaging, issue.receivedPackaging]
                : [];
        case SupplyOrderProductIssueType.BrokenQuantity: {
            if (!('brokenQuantity' in issue)) return [];
            const product = supplyOrder.products.find(({ _id }) => _id === issue.productId);
            const { quantityReceived } = getSupplyOrderProductReceivedTotal(product?.productReceivedDetails ?? []);
            return [
                getProductName(issue.productId),
                quantityReceived ?? 'N/A',
                issue.brokenQuantity,
                quantityReceived === null ? 'N/A' : quantityReceived - issue.brokenQuantity,
            ];
        }
        case SupplyOrderProductIssueType.NewBarcode:
            return 'barcode' in issue && 'productId' in issue ? [getProductName(issue.productId), issue.barcode] : [];
        case SupplyOrderProductIssueType.WrongProduct:
            return 'barcode' in issue && 'productId' in issue
                ? [getProductName(issue.productId), issue.productId, issue.barcode]
                : [];
        case SupplyOrderProductIssueType.UnknownProduct:
            return 'description' in issue ? [issue.description, issue.barcode] : [];
    }
}
